/**
 * The workflow sections' slot rules, as pure functions — what `ThreadsPane` does
 * with the sections' views, testable without rendering it.
 *
 * One side panel at a time, keyed by section id (`null`: none open).
 */

import type { ReactNode } from "react";
import type { WorkflowSectionView } from "../workflows/types";

/** Open this section's panel, closing whichever was open. */
export function openPanel(_current: string | null, id: string): string {
  return id;
}

/** Close this section's panel — and only it: another one that is open stays open. */
export function closePanel(current: string | null, id: string): string | null {
  return current === id ? null : current;
}

/** The rail button: closes its own open panel, otherwise opens it. */
export function togglePanel(current: string | null, id: string): string | null {
  return current === id ? null : id;
}

export interface SectionSlot {
  id: string;
  /** The workflow on screen declares this section. */
  enabled: boolean;
  view: WorkflowSectionView;
}

export interface ComposedRailAction {
  id: string;
  label: string;
  icon: ReactNode;
  open: boolean;
  disabled?: boolean;
}

export interface ComposedSections {
  requiredMode: WorkflowSectionView["requiredMode"];
  composerLocked: boolean;
  /** The first section's title for the conversation's first user message. */
  messageTitle: string | undefined;
  /** Any section hides Save Skill for this conversation. */
  hideSaveSkill: boolean;
  /** The open section's panel, or null. */
  panel: WorkflowSectionView["panel"];
  railActions: ComposedRailAction[];
  cards: { id: string; card: NonNullable<WorkflowSectionView["card"]> }[];
}

/**
 * What the pane shows, from every slot. A slot whose section the workflow on
 * screen does not declare contributes **nothing**, whatever its hook returned —
 * that is the promise that a conversation outside a workflow is the core chat.
 */
export function composeSections(slots: readonly SectionSlot[], open: string | null): ComposedSections {
  const live = slots.filter((slot) => slot.enabled);
  return {
    requiredMode: live.find((slot) => slot.view.requiredMode)?.view.requiredMode,
    composerLocked: live.some((slot) => slot.view.composerLocked === true),
    messageTitle: live.find((slot) => slot.view.messageTitle)?.view.messageTitle,
    hideSaveSkill: live.some((slot) => slot.view.hideSaveSkill === true),
    panel: live.find((slot) => slot.id === open && slot.view.panel)?.view.panel ?? null,
    railActions: live.flatMap((slot) =>
      slot.view.rail
        ? [{
            id: slot.id,
            label: slot.view.rail.label,
            icon: slot.view.rail.icon,
            open: open === slot.id,
            ...(slot.view.rail.disabled ? { disabled: true } : {}),
          }]
        : [],
    ),
    cards: live.flatMap((slot) => (slot.view.card ? [{ id: slot.id, card: slot.view.card }] : [])),
  };
}

// --- retrying a refused section send on its thread ------------------------------

/** A section send the agent refused after creating its thread. */
export interface PendingRetry {
  /** The chat it was sent from (`ChatSession.key`). */
  key: string;
  threadId: string;
  signature: string;
}

/** What makes two sends "the same send": the text, the attachments and the section's body. */
export function retrySignature(text: string | undefined, attachments: readonly unknown[], body: unknown): string {
  return JSON.stringify([text, attachments, body ?? null]);
}

/**
 * The thread a send should reuse, or null. Only a section send (one with a
 * body), only from the chat that was refused, only with the same signature — an
 * ordinary send never lands on a remembered thread.
 */
export function retryThread(
  pending: PendingRetry | null,
  send: { key: string; signature: string; hasBody: boolean },
): string | null {
  if (!send.hasBody || !pending) return null;
  return pending.key === send.key && pending.signature === send.signature ? pending.threadId : null;
}

/** What to remember after a failed send: its thread, when the refusal names one; otherwise nothing. */
export function rememberRefusal(
  send: { key: string; signature: string; hasBody: boolean },
  details: Record<string, unknown> | undefined,
): PendingRetry | null {
  const threadId = details?.["threadId"];
  return send.hasBody && typeof threadId === "string" && threadId ? { key: send.key, threadId, signature: send.signature } : null;
}
